/**
 * CreateReviewStep — "Your slate" review screen (specs/challenge-flow-redesign — Story 4).
 * Rival summary + pick rows (tap to edit in place, AC-4.7; remove) + coin stake stepper + Send Challenge.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { Image } from 'expo-image';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants';
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext';
import { useStakeStepper } from '../../../src/create/hooks/useStakeStepper';
import { VsHeader } from '../CreateBuildStep/VsHeader';
import { ReviewSlipEditor } from './ReviewSlipEditor';
import { SoccerKit } from '../../ui/SoccerKit';
import { TeamCrest } from '../../competitions/TeamCrest/TeamCrest';
import { SportIcon } from '../../../src/sports/components/SportIcon/SportIcon';
import { getSportDefinitionOrDefault, OUTCOME_SHAPES } from '../../../src/sports/sports.constants';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { slipRow } from '../../../src/create/helpers/reviewSlipRow.helper';
import { hasEnoughCoins } from '../../../src/shared/helpers/coins.helper';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import {
  CREATE_FLOW_TEST_IDS,
  OPEN_CHALLENGE_PARTICIPANT_ID,
} from '../../../src/create/constants/create.constants';
import { useFirstChallengeTarget } from '../../../src/onboarding/hooks/useFirstChallengeTarget';
import { FC_TARGET_KEYS } from '../../../src/onboarding/constants/firstChallengeTour.constants';
import { reviewStepStyles as styles } from './CreateReviewStep.styles';

export const CreateReviewStep: React.FC = () => {
  const {
    pickSlips,
    games,
    handleRemoveSlip,
    stakeCoins,
    setStakeCoins,
    userCoins,
    isGuestMonkeyCreateFlow,
    isSendingChallenge,
    handleSendChallenge,
    friends,
    selectedFriendId,
    selectedGroupId,
    selectedGroup,
    totalPoints,
  } = useCreatePage();

  // First-challenge tour: spotlight the coin stepper + Send (inert unless the tour is on those steps).
  const { viewRef: coinsRef } = useFirstChallengeTarget(FC_TARGET_KEYS.coins);
  const { viewRef: sendRef } = useFirstChallengeTarget(FC_TARGET_KEYS.send);
  const { viewRef: slateRef } = useFirstChallengeTarget(FC_TARGET_KEYS.slate);

  const gameById = useMemo(() => new Map(games.map((g) => [g.id, g])), [games]);

  // The one expanded row, by position (AC-4.7). An edit replaces the slip in place, but an O/U line
  // change swaps its marketId, so a slip-keyed state would collapse mid-edit; the index is stable.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const onRemoveSlip = useCallback(
    (slip: (typeof pickSlips)[number]) => {
      setExpandedIndex(null);
      handleRemoveSlip(slip);
    },
    [handleRemoveSlip]
  );

  // Group pot = stake × member count (server pot = stake × acceptedCount). Shown as "Max win"
  // because the real pot depends on how many members accept; a friend duel is always stake × 2.
  const participants = selectedGroupId
    ? Math.max(selectedGroup?.memberCount ?? selectedGroup?.memberIds?.length ?? 0, 2)
    : 2;

  const selectedFriend = selectedFriendId ? friends.find((f) => f.id === selectedFriendId) : undefined;

  // A wager can't exceed what the opponent can cover. The Monkey has no coins (server sends
  // null), so a coin stake against it is impossible. Group challenges have no single opponent
  // to cap against. `?? 0` fails safe: if a friend's balance failed to load, we block rather
  // than let an uncoverable stake through.
  const opponentCap = selectedGroupId != null
    ? undefined
    : selectedFriendId === OPEN_CHALLENGE_PARTICIPANT_ID
      // Open challenges have no named opponent to cap against, so the only ceiling is your own
      // balance (useStakeStepper already enforces it). The board hides a challenge from anyone who
      // cannot cover it, so a big stake narrows who can take it rather than stranding coins.
      ? undefined
      : selectedFriendId === MONKEY_USER_ID
        ? 0
        : selectedFriend?.coins ?? 0;

  const { stake, pot, canIncrement, canDecrement, increment, decrement } = useStakeStepper({
    stake: stakeCoins,
    balance: userCoins,
    setStake: setStakeCoins,
    ignoreBalance: isGuestMonkeyCreateFlow,
    opponentCap,
    participants,
  });

  // Backstop for the frame before the stepper clamps a stale stake down (see useStakeStepper):
  // never let the Send fire a stake the opponent can't cover. Covers the Monkey (cap 0) too.
  const insufficientOpponentCoins =
    opponentCap !== undefined && !hasEnoughCoins(opponentCap, stake);

  const hasRival = !!(selectedFriendId || selectedGroupId);
  const canSend =
    hasRival && pickSlips.length > 0 && !isSendingChallenge && !insufficientOpponentCoins;

  const onSend = useCallback(() => {
    if (!canSend) return;
    void handleSendChallenge();
  }, [canSend, handleSendChallenge]);

  return (
    <View style={styles.container} testID={CREATE_FLOW_TEST_IDS.reviewStep}>
      <View style={styles.header}>
        {/* No back button here — the page header's back is already step-aware (review → build). */}
        <Text style={styles.title}>Your slate</Text>
        <Text style={styles.scorePill}>
          score <Text style={styles.scoreVal}>{totalPoints.toFixed(2)}</Text>
        </Text>
      </View>

      <VsHeader />

      <View ref={slateRef} style={styles.scroll}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {pickSlips.map((slip, i) => {
          const game = gameById.get(slip.gameId);
          const { home, away, match, side, odds } = slipRow(slip, game);
          const rowSport = getSportDefinitionOrDefault(game?.sport);
          const rowUsesKits = rowSport.identityMode === 'KIT';
          const rowIsFieldSport = rowSport.shape === OUTCOME_SHAPES.FIELD;
          const homeKit = game?.homeKit ?? getTeamKitConfig(home);
          const awayKit = game?.awayKit ?? getTeamKitConfig(away);
          const isExpanded = expandedIndex === i;
          return (
            <View key={`${slip.gameId}-${i}`} style={styles.pickCard}>
              <View style={styles.pickRow}>
                <SoundPressable
                  tapSoundId={isExpanded ? SOUND_IDS.UI_SECTION_COLLAPSE : SOUND_IDS.UI_SECTION_EXPAND}
                  onPress={() => setExpandedIndex(isExpanded ? null : i)}
                  style={styles.pickToggle}
                  accessibilityRole="button"
                  accessibilityLabel={`Change pick: ${match}, ${side}`}
                  accessibilityState={{ expanded: isExpanded }}
                  testID={`create-review-row-${i}`}
                >
                  {/* Branch on shape and identity mode, never the sport key — same rule as the
                      build cards. A race gets one sport mark rather than a pair of crests, and a
                      basketball team gets its crest rather than a kit it does not wear. */}
                  {rowIsFieldSport ? (
                    <View style={styles.pickKits}>
                      <SportIcon sport={game?.sport} size={24} decorative />
                    </View>
                  ) : (
                    <View style={styles.pickKits}>
                      {rowUsesKits
                        ? <SoccerKit primary={homeKit.primary} secondary={homeKit.secondary} type={homeKit.type} size={24} />
                        : <TeamCrest teamName={home} sport={game?.sport} size={24} />}
                      <View style={styles.pickKitAway}>
                        {rowUsesKits
                          ? <SoccerKit primary={awayKit.primary} secondary={awayKit.secondary} type={awayKit.type} size={24} />
                          : <TeamCrest teamName={away} sport={game?.sport} size={24} />}
                      </View>
                    </View>
                  )}
                  <View style={styles.pickInfo}>
                    <Text style={styles.pickMatch} numberOfLines={1}>{match}</Text>
                    <Text style={styles.pickSide} numberOfLines={1}>{side}</Text>
                  </View>
                  <Text style={styles.pickOdds}>{odds.toFixed(2)}</Text>
                  {isExpanded
                    ? <ChevronUp size={16} color="#A78BFA" />
                    : <ChevronDown size={16} color="#A78BFA" />}
                </SoundPressable>
                <SoundPressable
                  tapSoundId={SOUND_IDS.UI_TAP_NAV}
                  onPress={() => onRemoveSlip(slip)}
                  style={styles.removeBtn}
                  testID={`create-review-remove-${i}`}
                >
                  <Text style={styles.removeText}>×</Text>
                </SoundPressable>
              </View>
              {isExpanded && (
                <ReviewSlipEditor slip={slip} game={game} homeName={home} awayName={away} />
              )}
            </View>
          );
        })}

      </ScrollView>
      </View>

      {/* Stake lives in the pinned footer (like the Send button) so it's visible without scrolling;
          kept to one compact row: stepper on the left, pot on the right. */}
      <View style={styles.footer}>
        {insufficientOpponentCoins && (
          <Text style={styles.warnText}>This opponent doesn&apos;t have enough coins for that stake.</Text>
        )}
        <View ref={coinsRef} style={styles.stakeBar} testID={CREATE_FLOW_TEST_IDS.stakeStepper}>
          <Text style={styles.stakeTitle}>Coins in</Text>
          <SoundPressable
            tapSoundId={SOUND_IDS.UI_TAP_STAKE}
            onPress={decrement}
            disabled={!canDecrement}
            style={[styles.stakeBtn, !canDecrement && styles.stakeBtnDisabled]}
            testID={CREATE_FLOW_TEST_IDS.stakeMinus}
          >
            <Text style={styles.stakeBtnText}>–</Text>
          </SoundPressable>
          <View style={styles.stakeValueBox}>
            <Image source={IDLE_COIN_SOURCE} style={styles.stakeCoin} alt={WEB_IMAGE_ALT_DECORATIVE} />
            <Text style={styles.stakeValue} testID={CREATE_FLOW_TEST_IDS.stakeValue}>{stake}</Text>
          </View>
          <SoundPressable
            tapSoundId={SOUND_IDS.UI_TAP_STAKE}
            onPress={increment}
            disabled={!canIncrement}
            style={[styles.stakeBtn, !canIncrement && styles.stakeBtnDisabled]}
            testID={CREATE_FLOW_TEST_IDS.stakePlus}
          >
            <Text style={styles.stakeBtnText}>+</Text>
          </SoundPressable>
          <View style={styles.stakeSpacer} />
          <Text style={styles.potLabel}>
            {selectedGroupId ? 'Max win ' : 'Winner takes '}
            <Text style={styles.potValue}>{pot}</Text>
          </Text>
        </View>
        <SoundPressable
          ref={sendRef}
          tapSoundId={SOUND_IDS.UI_TAP_NAV}
          onPress={onSend}
          disabled={!canSend}
          style={[styles.sendBtn, !canSend && styles.sendBtnDisabled]}
          testID={CREATE_FLOW_TEST_IDS.reviewSend}
        >
          <Text style={[styles.sendText, !canSend && styles.sendTextDisabled]}>
            {isSendingChallenge ? (isGuestMonkeyCreateFlow ? 'Sending…' : 'Creating…') : 'Send Challenge'}
          </Text>
        </SoundPressable>
      </View>
    </View>
  );
};
